import clsx from 'clsx';

import { SgComponentIcon } from '../../display/ComponentIcon';
import { SgButton } from '../SgButton';
import { useSgPickerInputContext } from './context';

/**
 * CLEAR BUTTON COMPONENT
 * =====================
 *
 * Provides a clear button for the SgPicker that:
 * - Only appears when there are selected items
 * - Shows/hides based on focus and open state for better UX
 * - Uses visibility instead of display for smooth transitions
 * - Integrates with the picker's context for consistent behavior
 * - Disabled when the picker is disabled or readonly
 */
export function ClearButton() {
  const {
    onClear: handleClear,
    size,
    selectedItems,
    selectedItem,
    isOpen,
    showClearButton,
    isHover,
    disabled,
    readonly,
  } = useSgPickerInputContext();

  /**
   * VISIBILITY LOGIC
   * ================
   *
   * The button visibility is controlled by multiple factors:
   * 1. Must have selections (single or multiple mode)
   * 2. showClearButton prop must be true
   * 3. Component must be focused or dropdown open (UX improvement)
   * 4. Component must NOT be disabled or readonly
   */
  const hasSelection = selectedItem || (selectedItems && selectedItems.length > 0);
  const show = showClearButton && hasSelection && (isHover || isOpen) && !disabled && !readonly;

  // Early return if clear button shouldn't be rendered at all
  if (!hasSelection || !showClearButton || disabled || readonly) {
    return null;
  }

  const classNames = clsx('d-flex align-items-center align-self-center h-100');

  /**
   * Using visibility instead of display/conditional rendering provides:
   * - Smoother transitions
   * - Consistent layout (space is reserved)
   * - Better accessibility (screen readers can track state changes)
   */
  const btnClassName = clsx({
    invisible: !show,
  });

  return (
    <div className={`${classNames} clear-button`}>
      <SgButton
        purpose="link"
        color="none"
        size={size}
        iconPosition="icon-only"
        className={btnClassName}
        onClick={handleClear}
        aria-label="Clear picker"
      >
        <SgComponentIcon icon="cancel" size={size} />
      </SgButton>
    </div>
  );
}
